import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { endOfDay, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import BotCdrRecordingModal from './BotCdrRecordingModal';
import {
  checkModulePermission,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';
import {
  botArchiveListGet,
  botCdrsDownloadRecording,
  botRecordingReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';

const PERMISSION_KEY = 'AI Recording';

// The header column whose value is a recording file path; rendered as play/download.
const RECORDING_FIELD = 'recording_file';

const renderCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const AiRecordingReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [botOptions, setBotOptions] = useState([]);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [recordingFile, setRecordingFile] = useState(null);

  // Default to the current-month range so the first (mount) call matches the Month=current
  // selection (1st 00:00:01 → now), instead of just today.
  const currentMonthLabel = `${today.toLocaleString('default', { month: 'long' })}-${today.getFullYear()}`;
  const defaultRange = getDateRangeFromLabel(currentMonthLabel, timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    startDate: defaultRange.minDate || startOfDay(today),
    endDate: defaultRange.maxDate || endOfDay(today),
    bot_id: '',
    destination_number: '',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required')
  });

  const onSubmit = async (valuesToUse) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = {
        page: valuesToUse.page,
        limit: valuesToUse.limit,
        start_date: formatPayloadDate(valuesToUse.startDate),
        end_date: formatPayloadDate(valuesToUse.endDate),
        archiveId: valuesToUse.archiveId
      };
      if (valuesToUse.bot_id) payload.bot_id = valuesToUse.bot_id;
      if (valuesToUse.destination_number)
        payload.destination_number = valuesToUse.destination_number.trim();

      const response = await dispatch(botRecordingReportGet(payload)).unwrap();
      setRows(Array.isArray(response?.data) ? response.data : []);
      setHeaders(Array.isArray(response?.header) ? response.header : []);
      // total/page/limit are siblings of data[] in the response (no nested pagination object).
      setPagination({
        total: response?.total ?? 0,
        page: response?.page ?? valuesToUse.page,
        limit: response?.limit ?? valuesToUse.limit
      });
    } catch (error) {
      console.log('error', error);
      setRows([]);
      setHeaders([]);
      setPagination({});
    } finally {
      setIsLoading(false);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (v) => onSubmit(v)
    });

  useEffect(() => {
    onSubmit(values);
  }, [values.limit, values.page]);

  // Recording is fetched as an authenticated blob (via the /bot-cdrs/download endpoint, which
  // serves any recording by its full file path) and saved through an object URL.
  const handleDownloadRecording = async (filePath) => {
    if (!filePath) return;
    try {
      const blob = await dispatch(botCdrsDownloadRecording({ filename: filePath })).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop() || 'recording';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
      console.log('error', error);
    }
  };

  const getBotOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      setBotOptions(
        response?.data ? response.data.map((val) => ({ label: val.name, value: val.id })) : []
      );
    } catch (error) {
      setBotOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(botArchiveListGet()).unwrap();
      const list = Array.isArray(response) ? response : response?.data || [];
      // Current month sits on top with value 0 (= both/current archive tables), mirroring the
      // Dump Report "Month" selector; the rest come from /bot-archive/ (id + archive_name).
      const currentLabel = `${today.toLocaleString('default', { month: 'long' })}-${today.getFullYear()}`;
      setArchiveOptions([
        { label: currentLabel, value: 0 },
        ...list.map((val) => ({ label: val.archive_name, value: val.id }))
      ]);
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBotOptions();
    getArchiveOptions();
  }, []);

  // Month selection drives the allowed date range (same behavior as the Dump Report):
  // the selected label ("Month-Year") is parsed into min/max dates that bound the pickers
  // and pre-fill the From/To dates.
  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );
  const selectedLabel = selectedOption?.label || null;
  const { minDate, maxDate } = useMemo(
    () => getDateRangeFromLabel(selectedLabel, timezone),
    [selectedLabel, timezone]
  );

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('startDate', minDate);
      setFieldValue('endDate', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="AI Recording Report"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />

      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="startDate"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  value={values.startDate}
                  minDate={minDate}
                  maxDate={values.endDate || maxDate}
                  onChange={(e) => setFieldValue('startDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="endDate"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.startDate || minDate}
                  maxDate={maxDate}
                  value={values.endDate}
                  onChange={(e) => setFieldValue('endDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  name="bot_id"
                  label="Bot"
                  placeholder="Select Bot"
                  value={values.bot_id}
                  options={botOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormInput
                  name="destination_number"
                  label="Destination Number"
                  placeholder="Enter destination number"
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 370px)' : 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {headers.map((col) => (
                    <th
                      key={col.name}
                      className={col.name === RECORDING_FIELD ? 'text-center' : ''}
                      style={col.name === RECORDING_FIELD ? { width: '120px' } : undefined}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row?.session_id || index}>
                    {headers.map((col) => {
                      if (col.name === RECORDING_FIELD) {
                        const recFile = row?.[col.name];
                        return (
                          <td key={col.name} className="text-center">
                            {recFile ? (
                              <div className="d-flex justify-content-center gap-4">
                                <Icon.PlayCircle
                                  size={16}
                                  title="Play recording"
                                  onClick={() => setRecordingFile(recFile)}
                                  className="cursor-pointer text-info"
                                />
                                <Icon.Download
                                  size={16}
                                  title="Download recording"
                                  className="cursor-pointer"
                                  onClick={() => handleDownloadRecording(recFile)}
                                />
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                        );
                      }
                      return <td key={col.name}>{renderCell(row?.[col.name])}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={pagination?.total || 0}
              limit={pagination?.limit || values.limit}
              page={pagination?.page || values.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit(values)}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}

      <BotCdrRecordingModal
        isOpen={!!recordingFile}
        filePath={recordingFile}
        onClose={() => setRecordingFile(null)}
      />
    </div>
  );
};

export default AiRecordingReport;
